import {
  type ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  ViewContainerRef,
  effect,
  inject,
  input,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import {
  EMPTY,
  filter,
  fromEvent,
  map,
  merge,
  pairwise,
  startWith,
  tap,
} from 'rxjs';
import { ControlErrorComponent } from './control-error.component';
import {
  FormErrorHandlerDirective,
  SUBMITTED_CLASS,
} from './form-error-handler.directive';
import { FORM_ERRORS, type FormErrors } from './form-error-provider';
import { FormField } from '@angular/forms/signals';

@Directive({
  selector: '[controlErrors]',
})
export class ControlErrorHandlerDirective {
  readonly controlErrors = input<FormErrors, FormErrors | string>(
    {},
    {
      transform: (value) => (typeof value === 'string' ? {} : value),
    },
  );
  readonly hint = input('');
  readonly #parent = inject(FormErrorHandlerDirective, {
    host: true,
    optional: true,
  });

  readonly #host: HTMLElement = inject(ElementRef).nativeElement;
  readonly #errorMap = inject(FORM_ERRORS);
  readonly #control = inject(NgControl, { self: true, optional: true });
  readonly #newControl = inject(FormField, { self: true, optional: true });

  readonly #vcr = inject(ViewContainerRef);

  readonly #submit$ = this.#parent?.submit$ || EMPTY;
  readonly #reset$ = this.#parent?.reset$ || EMPTY;

  readonly #destroy = inject(DestroyRef);

  #ref?: ComponentRef<ControlErrorComponent>;

  constructor() {
    // Shared submitted-state toggling: both the reactive and signal paths read
    // this host class, so it must run independently of the legacy NgControl effect.
    merge(
      this.#submit$.pipe(tap(() => this.#host.classList.add(SUBMITTED_CLASS))),
      this.#reset$.pipe(
        tap(() => this.#host.classList.remove(SUBMITTED_CLASS)),
      ),
    )
      .pipe(takeUntilDestroyed(this.#destroy))
      .subscribe();
  }

  readonly changes = effect(() => {
    // Signal-forms fields are handled by newControlChanges; skip the legacy path
    // so it no longer creates a throwaway ControlErrorComponent.
    if (this.#newControl) {
      return;
    }
    this.#ref?.destroy();
    this.#ref = this.#vcr.createComponent(ControlErrorComponent);
    this.#ref.setInput('hint', this.hint());
    const errorsShown$ = merge(
      fromEvent(this.#host, 'focusout').pipe(
        takeUntilDestroyed(this.#destroy),
        filter(() => !!this.#control?.dirty),
        map(() => true),
      ),
      this.#controlChanges(),
      this.#submit$.pipe(map(() => true)),
      this.#reset$.pipe(map(() => false)),
    );

    errorsShown$.pipe(takeUntilDestroyed(this.#destroy)).subscribe({
      next: this.#handleErrors,
      complete: () => this.#ref?.destroy(),
    });
  });

  // Compat layer as #controlChanges does not run on formField because there is no ng control
  readonly newControlChanges = effect(() => {
    const state = this.#newControl?.state();
    if (!state) {
      return;
    }
    const errors = state.errors();
    const show =
      errors.length > 0 &&
      (state.touched() || this.#host.classList.contains(SUBMITTED_CLASS));

    let error = '';
    if (show) {
      const { kind } = errors[0];
      const getError = this.controlErrors()?.[kind] || this.#errorMap[kind];
      if (getError) {
        const val =
          typeof getError === 'string' ? getError : getError(errors[0]);
        error = typeof val === 'string' ? val : val();
      }
    }

    // Create the component once there is something to render, then reuse it —
    // updating inputs instead of tearing it down avoids per-emission flicker.
    if (!this.#ref && !error && !this.hint()) {
      return;
    }
    this.#ref ??= this.#vcr.createComponent(ControlErrorComponent);
    this.#ref.setInput('hint', this.hint());
    this.#ref.setInput('disabled', state.disabled());
    this.#ref.setInput('error', error);
  });

  #handleErrors = (isShown: boolean) => {
    this.#ref?.setInput('disabled', this.#control?.disabled);
    if (!isShown) {
      this.#ref?.setInput('error', '');
      return;
    }
    const errors = this.#control?.errors;
    if (!errors) {
      return;
    }
    const [key, value] = Object.entries(errors)[0];
    const getError = this.controlErrors()?.[key] || this.#errorMap[key];
    if (getError) {
      const val = typeof getError === 'string' ? getError : getError(value);
      const text = typeof val === 'string' ? val : val();

      this.#ref?.setInput('error', text);
    }
  };

  #controlChanges = () =>
    this.#control?.statusChanges?.pipe(
      startWith(this.#control.status),
      takeUntilDestroyed(this.#destroy),
      pairwise(),
      map(([prev, curr]) => {
        if (curr === 'DISABLED') {
          return false;
        }
        if (
          prev === 'DISABLED' &&
          curr === 'INVALID' &&
          ((this.#control?.touched && this.#control.dirty) ||
            this.#host.classList.contains(SUBMITTED_CLASS))
        ) {
          return true;
        }
        return (
          curr === 'INVALID' &&
          (!!this.#control?.touched ||
            this.#host.classList.contains(SUBMITTED_CLASS))
        );
      }),
    ) || EMPTY;
}
